/**
 * Cookie consent — luxury bottom bar (Accept / Reject / Manage) +
 * manage-preferences modal with Essential / Analytics / Marketing toggles.
 *
 * The banner only shows when `hasDecided === false`. The modal is also
 * triggered from the Footer "Cookie Settings" link so users can revise
 * their choice at any time.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { useConsent } from "@/context/ConsentContext";
import { useLang } from "@/context/LanguageContext";
import { CODE_TO_URL } from "@/lib/i18nRouter";
import TX from "@/components/TX";

const ManageModal = ({ open, onClose }) => {
  const { consent, update } = useConsent();
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);

  // Keep local state in sync if consent updates while modal is open
  useEffect(() => {
    if (open) {
      setAnalytics(consent?.analytics ?? false);
      setMarketing(consent?.marketing ?? false);
    }
  }, [open, consent]);

  if (!open) return null;

  const save = () => {
    update({ analytics, marketing });
    onClose();
  };

  return (
    <div
      data-testid="cookie-manage-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-modal-title"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-lg bg-noir border border-gold/30 shadow-2xl">
        <div className="px-8 pt-8 pb-6 border-b border-white/10">
          <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-2"><TX>Privacy Preferences</TX></div>
          <h2 id="cookie-modal-title" className="text-white text-2xl font-light tracking-wide"><TX>Manage Cookie Settings</TX></h2>
          <p className="text-white/50 text-sm mt-3 leading-relaxed font-light">
            <TX>We use cookies to deliver a refined experience, analyse usage, and tailor marketing. You may grant or withdraw consent at any time.</TX>
          </p>
        </div>

        <div className="px-8 py-6 space-y-6">
          <PreferenceRow
            title={<TX>Strictly Necessary</TX>}
            description={<TX>Required for the site to function — secure checkout, language preference, authentication. Always on.</TX>}
            checked={true}
            disabled={true}
            testid="cookie-toggle-essential"
          />
          <PreferenceRow
            title={<TX>Analytics</TX>}
            description={<TX>Google Analytics 4 — anonymised usage statistics to help us improve the experience.</TX>}
            checked={analytics}
            onChange={setAnalytics}
            testid="cookie-toggle-analytics"
          />
          <PreferenceRow
            title={<TX>Marketing</TX>}
            description={<TX>Meta & TikTok pixels — measure campaign performance and personalise advertising.</TX>}
            checked={marketing}
            onChange={setMarketing}
            testid="cookie-toggle-marketing"
          />
        </div>

        <div className="px-8 pb-8 pt-2 flex flex-col sm:flex-row gap-3 justify-end border-t border-white/10 pt-6">
          <button
            data-testid="cookie-manage-cancel"
            onClick={onClose}
            className="px-6 py-3 text-[11px] tracking-[0.28em] uppercase font-accent text-white/60 hover:text-white transition-colors"
          >
            <TX>Cancel</TX>
          </button>
          <button
            data-testid="cookie-manage-save"
            onClick={save}
            className="px-8 py-3 text-[11px] tracking-[0.28em] uppercase font-accent bg-gold text-noir hover:bg-gold/90 transition-colors"
          >
            <TX>Save Preferences</TX>
          </button>
        </div>
      </div>
    </div>
  );
};

const PreferenceRow = ({ title, description, checked, onChange, disabled, testid }) => (
  <div className="flex items-start justify-between gap-6">
    <div className="flex-1">
      <div className="text-white text-sm tracking-wide mb-1">{title}</div>
      <div className="text-white/45 text-xs leading-relaxed font-light">{description}</div>
    </div>
    <div className="pt-1">
      <Switch
        data-testid={testid}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className="data-[state=checked]:bg-gold data-[state=unchecked]:bg-white/15"
      />
    </div>
  </div>
);

export const CookieConsent = () => {
  const { hasDecided, acceptAll, rejectAll, manageOpen, openManage, closeManage } = useConsent();
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";

  const showBanner = !hasDecided;

  return (
    <>
      {showBanner && (
        <div
          data-testid="cookie-consent-banner"
          role="region"
          aria-label="Cookie consent"
          className="fixed bottom-0 inset-x-0 z-[100] bg-noir/95 backdrop-blur-md border-t border-gold/40 shadow-[0_-8px_24px_rgba(0,0,0,0.6)]"
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-5 flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="flex-1 min-w-0">
              <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-1"><TX>Cookies & Privacy</TX></div>
              <p className="text-white/70 text-[13px] leading-relaxed font-light">
                <TX>We use cookies to ensure essential site functionality, measure performance, and personalise marketing. Read our</TX>{" "}
                <Link to={`/${slug}/policies/privacy`} className="text-gold hover:underline"><TX>Privacy Policy</TX></Link>.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:gap-3 lg:justify-end shrink-0">
              <button
                data-testid="cookie-banner-manage"
                onClick={openManage}
                className="px-5 py-2.5 text-[11px] tracking-[0.28em] uppercase font-accent text-white/70 hover:text-gold border border-white/15 hover:border-gold/50 transition-colors"
              >
                <TX>Manage</TX>
              </button>
              <button
                data-testid="cookie-banner-reject"
                onClick={rejectAll}
                className="px-5 py-2.5 text-[11px] tracking-[0.28em] uppercase font-accent text-white/70 hover:text-white border border-white/15 hover:border-white/40 transition-colors"
              >
                <TX>Reject All</TX>
              </button>
              <button
                data-testid="cookie-banner-accept"
                onClick={acceptAll}
                className="px-6 py-2.5 text-[11px] tracking-[0.28em] uppercase font-accent bg-gold text-noir hover:bg-gold/90 transition-colors"
              >
                <TX>Accept All</TX>
              </button>
            </div>
          </div>
        </div>
      )}
      <ManageModal open={manageOpen} onClose={closeManage} />
    </>
  );
};

export default CookieConsent;
